import React, { useEffect } from 'react';
import { Form, FormProps, Input, Modal, notification } from 'antd';
import { useMutation } from '@tanstack/react-query';

import useModal from 'stores/useModal';
import { getMessageCode } from 'constants/validate';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import useBookingStore from 'stores/useBooking';
import API from 'services/api';
import { useTranslation } from 'react-i18next';

function ConfirmDeletePaymentModal() {
  const { t } = useTranslation();
  const MESSAGE_CODE = getMessageCode(t);
  const [form] = Form.useForm();
  const { isOpenConfirmDeletePayment, paymentId, setIsOpenConfirmDeletePayment } = useModal();
  const { bookingLineId } = useBookingStore();

  const { mutateAsync: mutateCancelPayment, isPending } = useMutation({
    mutationFn: (params: { payment_id: number; reason: string }) =>
      API.booking.cancelPayment(params.payment_id, params.reason)
  });

  useEffect(() => {
    if (!isOpenConfirmDeletePayment) {
      form.resetFields();
    }
  }, [form, isOpenConfirmDeletePayment]);

  const handleCancel = () => {
    setIsOpenConfirmDeletePayment(false);
  };

  const handleOk = () => {
    form.submit();
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    if (paymentId) {
      try {
        await mutateCancelPayment({
          payment_id: paymentId,
          reason: values?.reason
        });
        setIsOpenConfirmDeletePayment(false);
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLineId]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_PAYMENTS, bookingLineId]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_CASH_FLOW]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_CASH_FLOW_ALL]
        });
        notification.success({
          message: t('cashierPage.confirmDeletePayment.success')
        });
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  return (
    <Modal
      title={t('cashierPage.confirmDeletePayment.title')}
      className="modal-confirm-delete-payment"
      centered
      width={400}
      open={isOpenConfirmDeletePayment}
      okText={t('common.actions.confirm')}
      cancelText={t('common.actions.cancel')}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isPending}
      destroyOnClose
    >
      <Form
        form={form}
        name="confirm-delete-payment-form"
        layout="vertical"
        style={{ width: '100%' }}
        initialValues={{
          reason: ''
        }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          name="reason"
          rules={[
            {
              required: true,
              message: MESSAGE_CODE.REQUIRED_REASON_FIX_ROOM
            }
          ]}
        >
          <Input.TextArea
            rows={5}
            placeholder={t('cashierPage.confirmDeletePayment.reasonPlaceholder')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default ConfirmDeletePaymentModal;
